import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  LinkedIn,
  Instagram,
  LocationOn,
  Email,
  Phone,
  AccessTime,
  Launch,
  CheckCircleOutline,
  Description,
  Code,
  Psychology,
  RecordVoiceOver,
  BarChart,
  Compare,
  Assignment,
  Speed,
  Groups,
  Dns,
  AutoAwesome,
  RocketLaunch,
  School,
  WorkOutline,
  Business,
  SettingsInputComponent,
  InfoOutlined,
  HelpOutline,
  QuestionAnswer,
  Article,
  LiveTv,
  Policy,
  Gavel,
  Security,
  AccountTree,
  VerifiedUser,
  SupportAgent,
  CheckCircle,
  QueryStats,
  PieChart,
  TrendingUp,
} from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import { Link as RouterLink, useNavigate } from "react-router-dom";

const FooterLink = ({ to, children, icon: Icon, disabled }) => {
  if (disabled) {
    return (
      <Box
        sx={{
          color: "#CBD5E1",
          fontSize: "0.85rem",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 0.5,
          cursor: "default",
          userSelect: "none"
        }}
      >
        {Icon && <Icon sx={{ fontSize: 16, color: "#60A5FA" }} />}
        {children}
      </Box>
    );
  }
  return (
    <Link
      component={RouterLink}
      to={to}
      sx={{
        color: "#CBD5E1",
        textDecoration: "none",
        fontSize: "0.85rem",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        transition: "all 0.2s",
        "&:hover": {
          color: "#60A5FA",
          transform: "translateX(4px)",
        },
      }}
    >
      {Icon && <Icon sx={{ fontSize: 16, color: "#60A5FA" }} />}
      {children}
    </Link>
  );
};

const FeatureItem = ({ icon: Icon, text }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
    <Icon sx={{ fontSize: 18, color: "#60A5FA" }} />
    <Typography variant="body2" sx={{ color: "#CBD5E1", fontSize: "0.85rem" }}>
      {text}
    </Typography>
  </Box>
);

function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate=useNavigate();
  const socialIcons = [
    { Icon: XIcon, link: "https://x.com/EvolveX2025" },
    { Icon: LinkedIn, link: "https://www.linkedin.com/in/bindhu-sri-vankadaru-7162053a5/" },
    { Icon: Instagram, link: "https://www.instagram.com/evolvexinnovations?igsh=NGtwd2E3ZmZyZ2Jh" },
  ];

  const platformLinks = [
    { label: "AI Resume Screening", to: "/#why-exihire", icon: Description, disabled: false },
    { label: "AI Coding Assessments", to: "/#why-exihire", icon: Code, disabled: false },
    { label: "AI Technical Interviews", to: "/#why-exihire", icon: Psychology, disabled: false },
    { label: "AI HR Interviews", to: "/#why-exihire", icon: RecordVoiceOver, disabled: false },
    { label: "Automated Candidate Ranking", to: "/#why-exihire", icon: BarChart, disabled: false },
    { label: "Detailed Evaluation Reports", to: "/#why-exihire", icon: Assignment, disabled: false },
    { label: "Skill Gap Analysis", to: "/#why-exihire", icon: AutoAwesome, disabled: false },
    { label: "Hiring Analytics Dashboard", to: "/#why-exihire", icon: TrendingUp, disabled: false },
  ];

  const solutionsLinks = [
    { label: "Startup Hiring", to: "/#built-for-teams", icon: RocketLaunch, disabled: false },
    { label: "Campus Recruitment", to: "/#built-for-teams", icon: School, disabled: false },
    { label: "Bulk Hiring", to: "/#built-for-teams", icon: Groups, disabled: false },
    { label: "Technical Hiring", to: "/#built-for-teams", icon: Code, disabled: false },
    { label: "Remote Hiring", to: "/#built-for-teams", icon: Business, disabled: false },
    { label: "Enterprise Recruitment", to: "/#built-for-teams", icon: SettingsInputComponent, disabled: false },
  ];

  const resourcesLinks = [
    { label: "How It Works", to: "/#how-it-works", icon: HelpOutline, disabled: false },
    { label: "Pricing", to: "/pricing", icon: TrendingUp, disabled: false },
    { label: "Help Center", to: "/contact", icon: InfoOutlined, disabled: false },
    { label: "FAQ", to: "/about#faq", icon: QuestionAnswer, disabled: false },
    { label: "Blog", to: "/blog", icon: Article, disabled: true },
    { label: "Platform Guide", to: "/#platform-overview", icon: VerifiedUser, disabled: false },
    { label: "Hiring Insights", to: "/#hiring-analytics", icon: QueryStats, disabled: false },
    { label: "API Documentation", to: "", icon: Code, disabled: true },
  ];

  const companyLinks = [
    { label: "About Us", to: "/about", icon: InfoOutlined, disabled: false },
    { label: "Contact Us", to: "/contact", icon: Email, disabled: false },
    { label: "Careers", to: "/careers", icon: WorkOutline, disabled: true },
    { label: "Privacy Policy", to: "/privacy-policy", icon: Policy, disabled: false },
    { label: "Terms of Service", to: "/terms-and-conditions", icon: Gavel, disabled: false },
    { label: "Security", to: "/#security", icon: Security, disabled: false },
    { label: "Sitemap", to: "/sitemap", icon: AccountTree, disabled: true },
  ];

  return (
    <Box
      component="footer"
      sx={{
        mt: "auto",
        width: "100%",
        bgcolor: "#020617",
        color: "white",
        borderTop: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Container maxWidth="xl" sx={{ pt: { xs: 6, md: 8, lg: 10 }, pb: 6 }}>
        <Grid container spacing={{ xs: 2, md: 3, lg: 4 }}>
          {/* Brand Section */}
          <Grid item xs={12} md={3} lg={3}>
            <Box sx={{ mb: 4 }}>
              <Box 
                component={RouterLink} 
                to="/" 
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, cursor: "pointer", textDecoration: 'none' }}
              >
                <img src="/logo-new.png?v=3" alt="ExiHire Logo" style={{ height: isMobile ? 45 : 60 }} />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.8,
                  color: "#94A3B8",
                  mb: 4,
                  fontSize: "0.9rem",
                }}
              >
                The premium AI hiring infrastructure platform. Streamlining your hiring process with automated coding tests, technical interviews, HR interviews, and intelligent candidate insights.
              </Typography>

              <Box sx={{ mb: 4 }}>
                <FeatureItem icon={Speed} text="AI-Powered Evaluations" />
                <FeatureItem icon={RocketLaunch} text="Faster Hiring Process" />
                <FeatureItem icon={QueryStats} text="Data-Driven Decisions" />
                <FeatureItem icon={Groups} text="Scalable for Every Team" />
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
               Follow Us
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                {socialIcons.map(({ Icon, link }, i) => (
                  <IconButton
                    key={i}
                    size="small"
                    component="a"
                    href={link}
                    target="_blank"
                    sx={{
                      color: "#94A3B8",
                      border: "1px solid rgba(255,255,255,0.1)",
                      transition: "all 0.3s",
                      "&:hover": {
                        color: "#60A5FA",
                        borderColor: "#60A5FA",
                        bgcolor: "rgba(96,165,250,0.05)",
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                  </IconButton>
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={4} md={1.7} lg={1.6}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Features
            </Typography>
            <Stack spacing={2}>
              {platformLinks.map((link) => (
                <FooterLink key={link.label} to={link.to} icon={link.icon} disabled={link.disabled}>
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={1.7} lg={1.6}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Solutions
            </Typography>
            <Stack spacing={2}>
              {solutionsLinks.map((link) => (
                <FooterLink key={link.label} to={link.to} icon={link.icon} disabled={link.disabled}>
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={1.7} lg={1.6}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Resources
            </Typography>
            <Stack spacing={2}>
              {resourcesLinks.map((link) => (
                <FooterLink key={link.label} to={link.to} icon={link.icon} disabled={link.disabled}>
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={6} sm={4} md={1.7} lg={1.6}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Company
            </Typography>
            <Stack spacing={2}>
              {companyLinks.map((link) => (
                <FooterLink key={link.label} to={link.to} icon={link.icon} disabled={link.disabled}>
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Contact Section */}
          <Grid item xs={12} sm={6} md={2.2} lg={2.2} sx={{ ml: { lg: -4 } }}>
            <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.05em" }}>
              Contact Us
            </Typography>
            <Stack spacing={3}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <LocationOn sx={{ color: "#60A5FA", fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "#94A3B8", lineHeight: 1.6, fontSize: "0.85rem" }}>
                  Bhoomatha Thalluri Heights, Kankipadu,<br />
                  Vijayawada-521151
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Email sx={{ color: "#60A5FA", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.85rem", wordBreak: "break-all" }}>
                  connect@exihire.com
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Phone sx={{ color: "#60A5FA", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.85rem" }}>
                  +91 62815 97586
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <AccessTime sx={{ color: "#60A5FA", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.85rem" }}>
                  24/7 Support Available
                </Typography>
              </Box>
              
              <Box onClick={()=>navigate("/contact")}
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "rgba(96,165,250,0.05)",
                  border: "1px solid rgba(96,165,250,0.2)",
                  borderRadius: 2,
                  p: 1.5,
                  color: "white",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(96,165,250,0.1)",
                    borderColor: "#60A5FA",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <SupportAgent sx={{ color: "#60A5FA" }} />
                  <Typography variant="body2" fontWeight={600}>Support Portal</Typography>
                </Box>
                <Launch sx={{ fontSize: 16, color: "#60A5FA" }} />
              </Box>


            </Stack>
          </Grid>
        </Grid>


        <Divider sx={{ mb: 4, borderColor: "rgba(255,255,255,0.05)" }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.85rem" }}>
            © 2026 ExiHire. All rights reserved.
          </Typography>
          
          <Stack direction="row" spacing={3} divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)", height: 12, my: "auto" }} />}>
            <Typography
              component={RouterLink}
              to="/privacy-policy"
              variant="body2"
              sx={{ color: "#64748B", fontSize: "0.85rem", textDecoration: "none", "&:hover": { color: "#94A3B8" } }}
            >
              Privacy Policy
            </Typography>
            <Typography
              component={RouterLink}
              to="/terms-and-conditions"
              variant="body2"
              sx={{ color: "#64748B", fontSize: "0.85rem", textDecoration: "none", "&:hover": { color: "#94A3B8" } }}
            >
              Terms of Service
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.85rem", cursor: "default" }}>
              Security
            </Typography>
          </Stack>

          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>


            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box component="span" sx={{ px: 1, py: 0.2, bgcolor: "white", borderRadius: 0.5, color: "#1A1F71", fontWeight: 900, fontSize: "0.6rem" }}>VISA</Box>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Box component="span" sx={{ width: 8, height: 8, bgcolor: "#EB001B", borderRadius: "50%" }} />
                <Box component="span" sx={{ width: 8, height: 8, bgcolor: "#F79E1B", borderRadius: "50%" }} />
              </Box>
              <Box component="span" sx={{ px: 1, py: 0.2, bgcolor: "#1E3A8A", borderRadius: 0.5, color: "white", fontWeight: 700, fontSize: "0.6rem" }}>UPI</Box>

            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
